import { createAsyncThunk } from '@reduxjs/toolkit';
import api from '../api';
import { createWrapperPayload, wrapperConstants } from '../wrapper-api';

export interface WellCompletionData {
  WellId: number;
  LiftTypeDescription: string;
  InstallNumber: string;
  InstallDate: string;
  StartDate: string;
  FailDate: string | null;
  PullDate: string | null;
  Status: string;
  EspEquipment?: {
    DisplayEquipment: string;
  };
  GLEquipment?: {
    DisplayEquipment: string;
  };
  GAPLEquipment?: {
    DisplayEquipment: string;
  };
  HasCompletion: boolean;
  InstallationId: number;
}

export const fetchWellCompletionData = createAsyncThunk(
  'wellCompletions/fetchWellCompletionData',
  async (wellId: number) => {
    try {
      const requestPayload = createWrapperPayload('GET', `/pumpchecker/api/v1/installation/well/${wellId}`);

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching well completion data:', error);
      return {};
    }
  },
);

// Fetch completion warnings data
export const fetchCompletionWarningsData = createAsyncThunk(
  'wellCompletions/fetchCompletionWarningsData',
  async ({ wellId, MOP }: { wellId: number; MOP: string }) => {
    try {
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/Well/GetCompletionWarnings?wellId=${wellId}&mop=${MOP}`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching historical completion data:', error);
      return {};
    }
  },
);

export const fetchCombinedCompletion = createAsyncThunk(
  'completions/fetchCombinedCompletion',
  async ({
    wellId,
    installationId,
    originalMop,
    newMop,
  }: {
    wellId: number;
    installationId: number;
    originalMop?: number;
    newMop?: number;
  }) => {
    try {
      let url = `/pumpchecker/api/v1/completion/getCombinedCompletion?wellId=${wellId}&installationId=${installationId}`;

      if (originalMop !== undefined) {
        url += `&originalMop=${originalMop}`;
      }

      if (newMop !== undefined) {
        url += `&newMop=${newMop}`;
      }

      const requestPayload = createWrapperPayload('GET', url);

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching Combined Completion:', error);
      return {};
    }
  },
);

export const fetchLiftTypesEnum = createAsyncThunk('completions/fetchLiftTypesEnum', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetLiftTypesEnum`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching Lift Types Enum:', error);
    return {};
  }
});

export const fetchManufacturers = createAsyncThunk('completions/fetchManufacturers', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetManufacturers`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching Manufacturers:', error);
    return {};
  }
});

export const fetchPumpManufacturers = createAsyncThunk('completions/fetchPumpManufacturers', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetPumpManufacturers`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching Pump Manufacturers:', error);
    return {};
  }
});

export const fetchManufacturerControllers = createAsyncThunk('completions/fetchManufacturerControllers', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetManufacturerController`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching Manufacturer Controllers:', error);
    return {};
  }
});

export const fetchManufacturersWithControllers = createAsyncThunk(
  'completions/fetchManufacturersWithControllers',
  async () => {
    try {
      const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetManufacturersWithControllers`);

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching Manufacturers with Controllers:', error);
      return {};
    }
  },
);

export const fetchDefaultBore = createAsyncThunk('completions/fetchDefaultBore', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/api/v1/completion/GetDefaultBore`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching Default Bore:', error);
    return {};
  }
});

export const fetchValveManufacturers = createAsyncThunk('completions/fetchValveManufacturers', async () => {
  try {
    const requestPayload = createWrapperPayload('GET', `/pumpchecker/Lookup/GetValveManufacturers`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error fetching Valve Manufacturers:', error);
    return {};
  }
});

export const createCombinedCompletion = createAsyncThunk(
  'completions/createCombinedCompletion',
  async (payload: any, { rejectWithValue }) => {
    try {
      const requestPayload = createWrapperPayload(
        'POST',
        `/pumpchecker/api/v1/completion/createCombinedCompletion`,
        payload,
      );

      const response = await api.post(wrapperConstants.router, requestPayload);
      return response.data;
    } catch (error: any) {
      if (error.response) {
        return rejectWithValue(error.response.data);
      }

      return rejectWithValue(error.message || 'Unknown error');
    }
  },
);

export const editCombinedCompletion = createAsyncThunk('completions/editCombinedCompletion', async (payload: any) => {
  try {
    const requestPayload = createWrapperPayload(
      'POST',
      `/pumpchecker/api/v1/completion/editCombinedCompletion`,
      payload,
    );

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error editing Combined Completion:', error);
    throw error;
  }
});
export const fetchPumpManufacturerSeries = createAsyncThunk(
  'completions/fetchPumpManufacturerSeries',
  async ({ field, manufacturer }: { field: string; manufacturer: string }) => {
    try {
      console.log(field, manufacturer);
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/Lookup/GetPumpManufacturerSeries?manufacturer=${manufacturer}`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching Manufacturer Series:', error);
      return {};
    }
  },
);
export const fetchPumpManufacturerTypes = createAsyncThunk(
  'completions/fetchPumpManufacturerTypes',
  async ({ field, manufacturer, series }: { field: string; manufacturer: string; series: string }) => {
    try {
      console.log(field, manufacturer, series);
      const requestPayload = createWrapperPayload(
        'GET',
        `/pumpchecker/Lookup/GetPumpManufacturerTypes?manufacturer=${manufacturer}&series=${series}`,
      );

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error fetching Manufacturer Types:', error);
      return {};
    }
  },
);

export const validateValvesCompletion = createAsyncThunk(
  'completions/validateValvesCompletion',
  async (payload: any) => {
    try {
      const requestPayload = createWrapperPayload('POST', `/pumpchecker/api/v1/completion/ValidateValves`, payload);

      return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
        if (response) {
          return response.data;
        } else {
          return undefined;
        }
      });
    } catch (error) {
      console.error('Error validating Valves Completion:', error);
      throw error;
    }
  },
);

export const deleteESPCompletions = createAsyncThunk('completions/deleteESPCompletions', async (id: any) => {
  try {
    const requestPayload = createWrapperPayload('POST', `/pumpchecker/Checker/Completions/Delete/${id}?redirect=false`);

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error deleting ESP Completions:', error);
    throw error;
  }
});

export const deleteGLCompletions = createAsyncThunk('completions/deleteGLCompletions', async (id: any) => {
  try {
    const requestPayload = createWrapperPayload(
      'POST',
      `/pumpchecker/GasLiftConfigurations/DeleteData/${id}?redirect=false`,
    );

    return await api.post(wrapperConstants.router, requestPayload).then(function (response) {
      if (response) {
        return response.data;
      } else {
        return undefined;
      }
    });
  } catch (error) {
    console.error('Error deleting Gas Lift Completions:', error);
    throw error;
  }
});
